import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Banknote,
  Copy,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  DollarSign,
  Users,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminPayoutsPage() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null); // payout being confirmed
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payouts");
      setPayouts(data.payouts);
    } catch {
      toast.error("Erro ao carregar saques");
    } finally {
      setLoading(false);
    }
  };

  const markPaid = async (payout) => {
    setBusy(true);
    try {
      await api.post(`/admin/payouts/${payout.id}/mark-paid`, { note: "" });
      toast.success(`Saque de ${payout.user_email} marcado como pago!`);
      setConfirming(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao marcar como pago"));
    } finally {
      setBusy(false);
    }
  };

  const copyPix = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("Chave PIX copiada!");
  };

  const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

  const pending = payouts.filter((p) => p.status === "requested");
  const paid = payouts.filter((p) => p.status === "paid");
  const totalPending = pending.reduce((s, p) => s + p.total_amount, 0);

  if (loading) {
    return (
      <div className="p-12 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono-label text-zinc-500 mb-1">Admin — Afiliados</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Saques Pendentes</h1>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm h-10 gap-2">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-sm bg-amber-50 text-amber-600 flex items-center justify-center">
              <Clock className="h-4 w-4" />
            </div>
            <span className="font-mono-label text-zinc-500">Aguardando pagamento</span>
          </div>
          <div className="font-display text-2xl font-bold tabular-nums">{pending.length}</div>
          <div className="text-xs text-zinc-500 mt-1">solicitações</div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-sm bg-amber-50 text-amber-600 flex items-center justify-center">
              <DollarSign className="h-4 w-4" />
            </div>
            <span className="font-mono-label text-zinc-500">Total a pagar</span>
          </div>
          <div className="font-display text-2xl font-bold tabular-nums text-amber-600">{fmt(totalPending)}</div>
          <div className="text-xs text-zinc-500 mt-1">em aberto</div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-sm bg-green-50 text-green-600 flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <span className="font-mono-label text-zinc-500">Já pagos</span>
          </div>
          <div className="font-display text-2xl font-bold tabular-nums text-green-600">{paid.length}</div>
          <div className="text-xs text-zinc-500 mt-1">saques processados</div>
        </div>
      </div>

      {/* Saques pendentes */}
      <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
        <Clock className="h-5 w-5 text-amber-500" /> Pendentes
      </h2>

      {pending.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center text-sm text-zinc-500 mb-8">
          Nenhum saque pendente no momento 🎉
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-8">
          <div className="grid grid-cols-[1fr_160px_220px_140px_140px] gap-4 px-5 py-3 border-b border-zinc-100 font-mono-label text-zinc-500 bg-zinc-50 text-xs">
            <span>Afiliado</span>
            <span>Valor</span>
            <span>Chave PIX</span>
            <span>Solicitado em</span>
            <span></span>
          </div>
          {pending.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-[1fr_160px_220px_140px_140px] gap-4 px-5 py-4 items-center border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
            >
              <div>
                <div className="text-sm font-semibold truncate">{p.user_name || p.user_email}</div>
                <div className="text-xs text-zinc-400 truncate">{p.user_email}</div>
              </div>
              <div className="font-mono text-sm font-bold text-amber-600">{fmt(p.total_amount)}</div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs bg-zinc-100 border border-zinc-200 rounded px-2 py-1 truncate max-w-[160px]">{p.pix_key}</span>
                <button
                  onClick={() => copyPix(p.pix_key)}
                  className="text-zinc-400 hover:text-zinc-700 flex-shrink-0"
                  title="Copiar chave PIX"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                {new Date(p.requested_at).toLocaleDateString("pt-BR")}
              </div>
              <div>
                <Button
                  onClick={() => setConfirming(p)}
                  size="sm"
                  className="rounded-sm bg-green-600 hover:bg-green-700 h-8 text-xs w-full"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Marcar pago
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Histórico pagos */}
      {paid.length > 0 && (
        <>
          <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" /> Histórico de pagos
          </h2>
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <div className="grid grid-cols-[1fr_140px_200px_140px_140px] gap-4 px-5 py-3 border-b border-zinc-100 font-mono-label text-zinc-500 bg-zinc-50 text-xs">
              <span>Afiliado</span>
              <span>Valor</span>
              <span>Chave PIX</span>
              <span>Pago em</span>
              <span>Por</span>
            </div>
            {paid.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-[1fr_140px_200px_140px_140px] gap-4 px-5 py-4 items-center border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
              >
                <div>
                  <div className="text-sm font-semibold truncate">{p.user_name || p.user_email}</div>
                  <div className="text-xs text-zinc-400 truncate">{p.user_email}</div>
                </div>
                <div className="font-mono text-sm font-semibold text-green-600">{fmt(p.total_amount)}</div>
                <div className="font-mono text-xs text-zinc-500 truncate">{p.pix_key}</div>
                <div className="text-xs text-zinc-500 font-mono">
                  {p.processed_at ? new Date(p.processed_at).toLocaleDateString("pt-BR") : "—"}
                </div>
                <div className="text-xs text-zinc-400 truncate">{p.processed_by || "—"}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal de confirmação */}
      <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Confirmar pagamento</DialogTitle>
            <DialogDescription>
              Você está confirmando que já realizou o PIX manualmente.
            </DialogDescription>
          </DialogHeader>
          {confirming && (
            <div className="py-3 space-y-3">
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Afiliado</span>
                  <span className="font-semibold">{confirming.user_name || confirming.user_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Valor</span>
                  <span className="font-bold text-green-600">{fmt(confirming.total_amount)}</span>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <span className="text-zinc-500">Chave PIX</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{confirming.pix_key}</span>
                    <button onClick={() => copyPix(confirming.pix_key)} className="text-zinc-400 hover:text-zinc-700">
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-zinc-400">
                Ao confirmar, o status do saque mudará para <strong>Pago</strong> e o afiliado poderá solicitar novos saques.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)} className="rounded-sm">
              Cancelar
            </Button>
            <Button
              onClick={() => markPaid(confirming)}
              disabled={busy}
              className="rounded-sm bg-green-600 hover:bg-green-700"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar — já paguei o PIX
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
